import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Star, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

function Stars({ value, size = "w-4 h-4" }) {
  return (
    <div className="flex">
      {Array.from({ length: 5 }).map((_, i) => (
        <Star key={i} className={`${size} ${i < value ? "fill-champagne text-champagne" : "text-espresso/25"}`} />
      ))}
    </div>
  );
}

export const ProductReviews = ({ slug, productId }) => {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [rating, setRating] = useState(5);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["reviews", slug],
    queryFn: async () => (await api.get(`/products/${slug}/reviews`)).data,
  });

  const reviews = data?.reviews || [];
  const count = data?.count || 0;
  const average = data?.average || 0;

  const submit = async (e) => {
    e.preventDefault();
    if (!comment.trim()) { toast.error("Please write a short comment."); return; }
    setSubmitting(true);
    try {
      const { data: res } = await api.post("/reviews", { product_id: productId, rating, comment });
      toast.success(res.message || "Review submitted for approval.");
      setComment(""); setRating(5);
      qc.invalidateQueries({ queryKey: ["reviews", slug] });
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="pt-1" data-testid="product-reviews">
      {/* Summary */}
      <div className="flex items-center gap-3 mb-5">
        <span className="font-serif text-3xl text-espresso" data-testid="reviews-average">{count ? average : "—"}</span>
        <div>
          <Stars value={Math.round(average)} />
          <p className="text-xs text-espresso/50 mt-0.5" data-testid="reviews-count">{count} {count === 1 ? "review" : "reviews"}</p>
        </div>
      </div>

      {/* List */}
      {isLoading ? (
        <p className="text-sm text-espresso/50">Loading reviews…</p>
      ) : reviews.length === 0 ? (
        <p className="text-sm text-espresso/55 mb-5" data-testid="reviews-empty">No reviews yet. Be the first to review this product.</p>
      ) : (
        <div className="space-y-5 mb-6">
          {reviews.map((r) => (
            <div key={r.id} className="border-b border-champagne/15 pb-4 last:border-0" data-testid={`review-item-${r.id}`}>
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-espresso">{r.user_name}</span>
                <span className="text-[11px] text-espresso/40">{r.created_at ? new Date(r.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : ""}</span>
              </div>
              <div className="mt-1"><Stars value={r.rating} /></div>
              {r.comment && <p className="text-sm text-espresso/70 mt-2 leading-relaxed">{r.comment}</p>}
            </div>
          ))}
        </div>
      )}

      {/* Write a review */}
      <div className="mt-2 border-t border-champagne/20 pt-5">
        {user ? (
          <form onSubmit={submit} data-testid="review-form">
            <p className="text-sm font-medium text-espresso mb-3">Write a review</p>
            <div className="flex items-center gap-1 mb-3" data-testid="review-rating-input">
              {Array.from({ length: 5 }).map((_, i) => {
                const val = i + 1;
                return (
                  <button type="button" key={val} onClick={() => setRating(val)} onMouseEnter={() => setHover(val)} onMouseLeave={() => setHover(0)} data-testid={`review-star-${val}`} aria-label={`${val} star`}>
                    <Star className={`w-6 h-6 transition-colors ${val <= (hover || rating) ? "fill-champagne text-champagne" : "text-espresso/25"}`} />
                  </button>
                );
              })}
            </div>
            <textarea value={comment} onChange={(e) => setComment(e.target.value)} rows={3} placeholder="Share your experience with this product…" className="w-full border border-espresso/20 rounded-2xl p-3 text-sm text-espresso focus:outline-none focus:border-champagne resize-none" data-testid="review-comment-input" />
            <button type="submit" disabled={submitting} className="mt-3 bg-espresso text-ivory px-6 py-3 rounded-full text-sm font-medium hover:bg-espresso/90 transition-colors disabled:opacity-50 flex items-center gap-2" data-testid="review-submit">
              {submitting && <Loader2 className="w-4 h-4 animate-spin" />} Submit Review
            </button>
            <p className="text-[11px] text-espresso/40 mt-2">Reviews appear after approval by our team.</p>
          </form>
        ) : (
          <p className="text-sm text-espresso/60" data-testid="review-login-prompt">
            Please <Link to="/login" className="text-champagne-dark underline font-medium">log in</Link> to write a review.
          </p>
        )}
      </div>
    </div>
  );
};
